import axios from 'axios';
import { AuthState } from '@okta/okta-auth-js';
import BaseService from './baseService';

export interface SubscriptionResponse {
    message: string;
    id?: string;
}

export interface UserSubscription {
    id: string;
    apiId: string;
    apiName: string;
    userEmail: string;
    notificationEmail?: string;
    createdBy: string;
    lastUpdated: string;
}

export default class SubscriptionService extends BaseService {

    constructor() {
        super();
    }

    /**
     * Generates a UI-safe link to the API details page using the frontend's current origin,
     * ensuring we don't accidentally route the user to the backend Express server.
     */
    public getApiDetailsUrl(apiId: string): string {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}/api-details/${encodeURIComponent(apiId)}`;
    }

    public async subscribeToApi(apiId: string, email: string, authState: AuthState | null): Promise<SubscriptionResponse> {
        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/subscriptions`,
                { apiId, email },
                this.getHeaders(authState)
            );
            return response.data as SubscriptionResponse;
        } catch (error) {
            console.error('Error subscribing to API updates', error);
            throw error;
        }
    }

    public async getMySubscriptions(authState: AuthState | null): Promise<UserSubscription[]> {
        try {
            const response = await axios.get(
                `${this.REACT_APP_API_BASE_URL}/subscriptions/my-subscriptions`,
                this.getHeaders(authState)
            );
            return response.data as UserSubscription[];
        } catch (error) {
            console.error('Error fetching user subscriptions', error);
            throw error;
        }
    }

    public async unsubscribe(subscriptionId: string, authState: AuthState | null): Promise<void> {
        try {
            await axios.delete(
                `${this.REACT_APP_API_BASE_URL}/subscriptions/${subscriptionId}`,
                this.getHeaders(authState)
            );
        } catch (error) {
            console.error(`Error unsubscribing from API with subscription id ${subscriptionId}`, error);
            throw error;
        }
    }

    public async unsubscribeBatch(subscriptionIds: string[], authState: AuthState | null): Promise<void> {
        if (!subscriptionIds.length) {
            return;
        }

        await Promise.all(subscriptionIds.map((id) => this.unsubscribe(id, authState)));
    }

    public async subscribeBatch(apiIds: string[], email: string, authState: AuthState | null): Promise<BatchSubscriptionResponse> {
        if (!apiIds.length) {
            return { successful: [], failed: [] };
        }

        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/subscriptions/batch`,
                { apiIds, email },
                this.getHeaders(authState)
            );
            return response.data as BatchSubscriptionResponse;
        } catch (error) {
            console.error('Error subscribing to APIs in batch', error);
            throw error;
        }
    }
}

export interface BatchSubscriptionResponse {
    successful: Array<{ apiId: string; subscriptionId: string }>;
    failed: Array<{ apiId: string; error: string }>;
}